import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import SystemBanner from "../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { OnboardingJob } from "../../../services/backend.service";
import { ACCEPTED_TYPES } from "../utils";
import { WeekParityConfirmDialog } from "../WeekParityConfirmDialog";
import { AutoPromptAnalysisPanel } from "./AutoPromptAnalysisPanel";
import { AutoPromptTransformPanel } from "./AutoPromptTransformPanel";
import {
	type AutoPromptFileKind,
	EFFORT_LABEL_KEY,
	EFFORT_ORDER,
	formatFileSize,
	KIND_LABEL_KEY,
	KIND_ORDER,
	useAutoPromptSection,
} from "./useAutoPromptSection";

type Props = {
	restoredJob?: OnboardingJob;
	onJobCreated?: (job: OnboardingJob) => void;
};

export function AutoPromptSection({ restoredJob, onJobCreated }: Props) {
	const state = useAutoPromptSection({ restoredJob, onJobCreated });
	const { t } = useTranslation();
	const filesLocked = state.phase !== "upload";

	const effortOptions = useMemo(
		() =>
			EFFORT_ORDER.map((value) => ({
				label: t(EFFORT_LABEL_KEY[value]),
				value,
				"data-testid": `auto-prompt-effort-${value}`,
				disabled: state.isAnalyzing || filesLocked,
			})),
		[t, state.isAnalyzing, filesLocked],
	);

	return (
		<Paper data-testid="auto-prompt-section" sx={{ p: { xs: 2, sm: 3 }, minHeight: "fit-content", overflow: "visible" }}>
			<Typography variant="h5" sx={{ mb: 1 }}>
				{t("onboarding_auto_prompt.title")}
			</Typography>
			<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
				{t("onboarding_auto_prompt.description")}
			</Typography>
			{state.artifactsUnavailable && (
				<SystemBanner
					tone="warning"
					title={t("onboarding_jobs.expired_title")}
					description={t("onboarding_jobs.expired_description")}
					action={
						<SystemButton buttonVariant="secondary" onClick={state.resetWizard} data-testid="auto-prompt-expired-rerun">
							{t("onboarding_jobs.expired_action")}
						</SystemButton>
					}
					sx={{ mb: 2 }}
					dataTestId="auto-prompt-expired-banner"
				/>
			)}

			{!filesLocked && !state.artifactsUnavailable && (
				<FileDropZone
					multiple
					accept={ACCEPTED_TYPES.join(",")}
					hint={t("onboarding_auto_prompt.drop_hint")}
					onFiles={state.addFiles}
					dataTestId="auto-prompt-drop-zone"
				/>
			)}

			{!state.artifactsUnavailable && state.files.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
					{state.files.map((file) => (
						<Paper key={file.id} variant="outlined" sx={{ p: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
							<Box sx={{ flex: 1, minWidth: 160 }}>
								<Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{file.file.name}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{formatFileSize(file.file.size)}
								</Typography>
							</Box>
							{filesLocked ? (
								<MetaChip label={t(KIND_LABEL_KEY[file.kind])} dataTestId={`auto-prompt-file-type-${file.id}`} />
							) : (
								<>
									<SystemDropdown<AutoPromptFileKind>
										label={t("onboarding_auto_prompt.file_type")}
										items={KIND_ORDER.map((kind) => ({ value: kind, label: t(KIND_LABEL_KEY[kind]) }))}
										selectedItem={file.kind}
										onChange={(value) => value && state.handleKindChange(file.id, value)}
										formControlSx={{ minWidth: 140 }}
										data-testid={`auto-prompt-file-kind-${file.id}`}
									/>
									<SystemButton
										buttonVariant="borderless"
										iconButton
										leadingIcon={<DeleteOutlineOutlinedIcon />}
										aria-label={t("onboarding_auto_prompt.remove_file", { file: file.file.name })}
										onClick={() => state.handleDeleteFile(file.id)}
										data-testid={`auto-prompt-remove-file-${file.id}`}
									/>
								</>
							)}
						</Paper>
					))}
				</Box>
			)}

			{!state.artifactsUnavailable && (state.phase === "upload" || state.phase === "analysis") && (
				<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, mb: 2 }}>
					<Box sx={{ display: "flex", gap: 1 }}>
						<SystemButton
							onClick={() => void state.handleAnalyze()}
							disabled={!state.hasAtLeastOneFile || !state.departmentId || state.isAnalyzing || (filesLocked && !state.analysisFailed)}
							loading={state.isAnalyzing}
							data-testid="auto-prompt-analyze-button"
						>
							{state.analysisFailed ? t("onboarding_auto_prompt.analyze_again") : t("onboarding_auto_prompt.analyze")}
						</SystemButton>
						{state.phase !== "upload" && (
							<SystemButton buttonVariant="secondary" onClick={state.resetWizard} disabled={state.isAnalyzing} data-testid="auto-prompt-reset-button">
								{t("onboarding_auto_prompt.start_over")}
							</SystemButton>
						)}
					</Box>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: { sm: "auto" } }}>
						<Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
							{t("onboarding_auto_prompt.effort.label")}
						</Typography>
						<SystemFlatSwitch
							options={effortOptions}
							activeIndexOrValue={state.effort}
							onSwitch={(option) => state.setEffort(option.value)}
							delayCallback={0}
							equalWidthOptions
							data-testid="auto-prompt-effort-switch"
						/>
					</Box>
					<Typography variant="caption" sx={{ flexBasis: "100%", color: "text.secondary" }}>
						{t("onboarding_auto_prompt.effort.hint")}
					</Typography>
				</Box>
			)}

			{!state.artifactsUnavailable && state.phase === "analysis" && state.editable && <AutoPromptAnalysisPanel state={state} />}
			{!state.artifactsUnavailable && (state.phase === "prompt" || state.phase === "transform") && <AutoPromptTransformPanel state={state} />}

			{!state.artifactsUnavailable && state.chosenDepartment?.first_sunday && (
				<WeekParityConfirmDialog
					open={state.showWeekParityDialog}
					department={state.chosenDepartment}
					today={DateTime.now().setZone(state.chosenDepartment.timezone)}
					isSwitching={state.isUpdatingFirstSunday}
					onSwitch={state.handleSwitchAndUploadTemplate}
					onKeepAsIs={state.handleKeepAsIsAndUploadTemplate}
					onCancel={() => state.setShowWeekParityDialog(false)}
				/>
			)}
		</Paper>
	);
}
